import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import Logo from "../components/Logo";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileText, TrendingUp, Target, MessageSquare, Users, Calendar, Brain, Sparkles, Archive, Trash2, RotateCcw, Trophy, UserCheck, X, ChevronDown, ChevronUp, RefreshCw, Loader2, ClipboardList } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AISuggestGoalsDialog from "../components/goals/AISuggestGoalsDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { icon: FileText, label: "My Training Plan", path: "/plans" },
  { icon: Brain, label: "Mental Training", path: "/training" },
  { icon: TrendingUp, label: "Mental Performance", path: "/performance" },
  { icon: Target, label: "Goals", path: "/goals" },
  { icon: MessageSquare, label: "Messages", path: "/messages" },
  { icon: MessageSquare, label: "AI Coach", path: "/ai-coach" }
];

export default function Goals({ user, onLogout }) {
  const [goals, setGoals] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [breathingPreference, setBreathingPreference] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    target_date: "",
    category: "personal-enhancement",
    milestones: []
  });
  const [updateProgress, setUpdateProgress] = useState(0);
  const [showAISuggest, setShowAISuggest] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [planData, setPlanData] = useState({});
  const [renewingPlan, setRenewingPlan] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoals(response.data);
    } catch (error) {
      if (error._handled401) return;
      toast.error("Failed to load goals");
    }
  };

  const togglePlanView = async (goalId, planId) => {
    if (expandedPlan === goalId) {
      setExpandedPlan(null);
      return;
    }
    setExpandedPlan(goalId);
    if (!planData[planId]) {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/ai/plans`, { headers: { Authorization: `Bearer ${token}` } });
        const plan = res.data.find(p => p.id === planId);
        if (plan) setPlanData(prev => ({ ...prev, [planId]: plan }));
      } catch { /* silent */ }
    }
  };

  const handleRenewPlan = async () => {
    setRenewingPlan(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/training-plans/renew`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Updated plan generating based on your check-in data!");
      setTimeout(() => { fetchGoals(); setRenewingPlan(false); }, 15000);
    } catch (err) {
      toast.error("Failed to generate updated plan");
      setRenewingPlan(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!formData.title || !formData.description || !formData.target_date || !formData.category) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/goals`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFormData({
        title: "",
        description: "",
        target_date: "",
        category: "personal-enhancement",
        milestones: []
      });

      toast.success("Goal created successfully!");
      setShowCreate(false);
      fetchGoals();
    } catch (error) {
      toast.error("Failed to create goal");
    }
  };

  const handleEditGoal = (goal) => {
    setSelectedGoal(goal);
    setFormData({
      title: goal.title,
      description: goal.description,
      target_date: goal.target_date.split('T')[0],
      category: goal.category,
      milestones: goal.milestones || []
    });
    setShowEdit(true);
  };

  const handleUpdateGoal = async () => {
    if (!formData.title || !formData.description || !formData.target_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/goals/${selectedGoal.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Goal updated successfully!");
      setShowEdit(false);
      setSelectedGoal(null);
      setFormData({
        title: "",
        description: "",
        target_date: "",
        category: "personal-enhancement",
        milestones: []
      });
      fetchGoals();
    } catch (error) {
      toast.error("Failed to update goal");
    }
  };

  const handleCreateTrainingPlan = async (goalId) => {
    setGeneratingPlan(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/training-plans/from-goal/${goalId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const plan = response.data;
      if (plan?.generation_status === 'generating') {
        toast.success("Your training plan is being generated! Redirecting to plans...");
      } else {
        toast.success("Training plan created!");
      }
      fetchGoals();
      
      const planId = plan?.id;
      setTimeout(() => {
        navigate(planId ? `/plans?planId=${planId}` : '/plans');
      }, 1200);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("Training plan already exists for this goal");
      } else {
        toast.error(error.response?.data?.detail || "Failed to create training plan");
      }
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleUpdateProgressFromPlan = async (goalId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/goals/${goalId}/update-progress`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Progress updated to ${response.data.progress}%`);
      fetchGoals(); // Refresh goals to show updated progress
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  const handleUpdateProgress = async () => {
    if (!selectedGoal) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/goals/${selectedGoal.id}`, {
        progress: updateProgress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Progress updated!");
      setSelectedGoal(null);
      setUpdateProgress(0);
      fetchGoals();
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm("Permanently delete this goal? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/goals/${goalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Goal deleted");
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete goal");
    }
  };

  const handleArchiveGoal = async (goalId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/goals/${goalId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Goal archived");
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive goal");
    }
  };

  const handleRestoreGoal = async (goalId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/goals/${goalId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Goal restored");
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore goal");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "text-blue-600 bg-blue-100",
      completed: "text-green-600 bg-green-100",
      abandoned: "text-red-600 bg-red-100",
      archived: "text-gray-500 bg-gray-100"
    };
    return colors[status] || colors.active;
  };

  const getCategoryColor = (category) => {
    const colors = {
      "personal-enhancement": "text-green-600",
      "team-enhancement": "text-blue-600"
    };
    return colors[category] || "text-gray-600";
  };

  const getCategoryLabel = (category) => {
    const labels = {
      "personal-enhancement": "Personal Enhancement",
      "team-enhancement": "Team Enhancement"
    };
    return labels[category] || category;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 sm:mb-8">
          <div>
            <h1 data-testid="goals-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
              Goals
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Set targets and track your mental performance journey
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="ai-suggest-goals-btn"
              onClick={() => setShowAISuggest(true)}
              variant="outline"
              className="border-violet-300 text-violet-700 hover:bg-violet-50 shadow-sm text-sm font-semibold"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              AI Suggest
            </Button>
            <Button
              data-testid="new-goal-btn"
              onClick={() => setShowCreate(true)}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-sm text-sm font-semibold"
            >
              <Target className="w-4 h-4 mr-1.5" />
              New Goal
            </Button>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { key: "all", label: "All Goals" },
            { key: "personal-enhancement", label: "Personal Enhancement" },
            { key: "team-enhancement", label: "Team Enhancement" },
            { key: "archived", label: "Archived", icon: Archive },
          ].map(f => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilterCategory(f.key)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold border transition-all duration-200 ${
                filterCategory === f.key
                  ? f.key === 'archived'
                    ? 'bg-gray-800 text-white border-transparent shadow-sm'
                    : 'bg-emerald-600 text-white border-transparent shadow-sm'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:shadow-sm'
              }`}
            >
              {f.icon && <f.icon className="w-3.5 h-3.5" />}
              {f.label}
            </button>
          ))}
        </div>

        {/* Goals Grid */}
        <div className="space-y-6">
          {goals
            .filter(goal => {
              if (filterCategory === "archived") return goal.status === "archived";
              if (filterCategory === "all") return goal.status !== "archived";
              return goal.category === filterCategory && goal.status !== "archived";
            })
            .map((goal) => (
            <Card
              key={goal.id}
              data-testid={`goal-card-${goal.id}`}
              className="border border-gray-100 shadow-sm bg-white hover:shadow-md hover:border-gray-200 transition-all duration-200 cursor-pointer rounded-xl"
              onClick={() => handleEditGoal(goal)}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${
                        goal.category === 'personal-enhancement'
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'bg-blue-50 text-blue-600'
                      }`}>
                        {getCategoryLabel(goal.category)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${getStatusColor(goal.status)}`}>
                        {goal.status}
                      </span>
                    </div>
                    <CardTitle className="text-base font-semibold text-gray-900 mb-1">{goal.title}</CardTitle>
                    <CardDescription className="text-sm text-gray-500 line-clamp-2">
                      {goal.description}
                    </CardDescription>
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-400">
                      <Calendar className="w-3 h-3" />
                      <span>Target: {new Date(goal.target_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs text-gray-400 font-medium">Progress</span>
                    <span className="text-xs font-bold text-gray-700">{goal.progress}%</span>
                  </div>
                  <Progress value={goal.progress} className="h-2" />
                </div>

                <div className="flex flex-wrap gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
                  <Button
                    onClick={() => handleEditGoal(goal)}
                    variant="outline"
                    size="sm"
                    className="border-emerald-300 hover:bg-emerald-50"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Goal
                  </Button>
                  
                  {!goal.linked_plan && (
                    <Button
                      onClick={() => handleCreateTrainingPlan(goal.id)}
                      disabled={generatingPlan}
                      variant="outline"
                      size="sm"
                      className="border-blue-300 hover:bg-blue-50"
                    >
                      <Brain className="w-4 h-4 mr-1" />
                      {generatingPlan ? "Creating..." : "Create AI Training Plan"}
                    </Button>
                  )}

                  {goal.linked_plan && (
                    <Button
                      data-testid={`view-linked-plan-${goal.id}`}
                      onClick={() => navigate(`/plans?planId=${goal.linked_plan.plan_id}`)}
                      variant="outline"
                      size="sm"
                      className="border-emerald-300 hover:bg-emerald-50 text-emerald-700"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      View Training Plan
                    </Button>
                  )}
                  
                  <Button
                    onClick={() => handleUpdateProgressFromPlan(goal.id)}
                    variant="outline"
                    size="sm"
                    className="border-purple-300 hover:bg-purple-50"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Sync Progress
                  </Button>

                  {goal.status !== "archived" ? (
                    <>
                      <Button
                        data-testid={`archive-goal-${goal.id}`}
                        onClick={() => handleArchiveGoal(goal.id)}
                        variant="outline"
                        size="sm"
                        className="border-gray-300 hover:bg-gray-50 text-gray-600"
                      >
                        <Archive className="w-4 h-4 mr-1" />
                        Archive
                      </Button>
                      <Button
                        data-testid={`delete-goal-${goal.id}`}
                        onClick={() => handleDeleteGoal(goal.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-300 hover:bg-red-50 text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </>
                  ) : (
                    <Button
                      data-testid={`restore-goal-${goal.id}`}
                      onClick={() => handleRestoreGoal(goal.id)}
                      variant="outline"
                      size="sm"
                      className="border-emerald-300 hover:bg-emerald-50 text-emerald-600"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Restore
                    </Button>
                  )}
                </div>
              </CardHeader>
            </Card>
          ))}

          {goals.filter(goal => {
              if (filterCategory === "archived") return goal.status === "archived";
              if (filterCategory === "all") return goal.status !== "archived";
              return goal.category === filterCategory && goal.status !== "archived";
            }).length === 0 && (
            <div className="text-center py-12">
              <Target className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No goals found</h3>
              <p className="text-gray-600 mb-4">
                {filterCategory === "all" 
                  ? "Create your first goal to start tracking your progress"
                  : `No ${getCategoryLabel(filterCategory).toLowerCase()} goals found`
                }
              </p>
              <Button onClick={() => setShowCreate(true)} className="bg-sky-600 hover:bg-sky-700">
                Create Goal
              </Button>
            </div>
          )}
        </div>

        {/* Create Goal Dialog */}
        <Dialog open={showCreate} onOpenChange={(open) => {
          setShowCreate(open);
          if (!open) setFormData({ title: "", description: "", target_date: "", category: "personal-enhancement", milestones: [] });
        }}>
          <DialogContent data-testid="create-goal-dialog" className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="block text-lg font-bold text-gray-900">Create New Goal</span>
                  <span className="block text-xs font-medium text-gray-400 mt-0.5">Define your mental performance target</span>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-2">
              {/* Goal Title */}
              <div>
                <Label htmlFor="title" className="text-sm font-semibold text-gray-700">Goal Title *</Label>
                <Input
                  id="title"
                  data-testid="goal-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Improve focus during pressure situations"
                  className="mt-1.5"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description" className="text-sm font-semibold text-gray-700">Description *</Label>
                <Textarea
                  id="description"
                  data-testid="goal-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what you want to achieve and how you'll measure success..."
                  className="mt-1.5"
                  rows={3}
                />
              </div>

              {/* Category Selection */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Category *</Label>
                <div className="grid grid-cols-2 gap-3 mt-1.5">
                  <button
                    data-testid="category-personal"
                    type="button"
                    onClick={() => setFormData({ ...formData, category: "personal-enhancement" })}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all duration-200 text-left ${
                      formData.category === "personal-enhancement"
                        ? "border-emerald-500 bg-emerald-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${formData.category === "personal-enhancement" ? "bg-emerald-100" : "bg-gray-100"}`}>
                      <Trophy className={`w-5 h-5 ${formData.category === "personal-enhancement" ? "text-emerald-600" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${formData.category === "personal-enhancement" ? "text-emerald-900" : "text-gray-700"}`}>Personal</p>
                      <p className="text-[11px] text-gray-400">Individual growth</p>
                    </div>
                  </button>
                  <button
                    data-testid="category-team"
                    type="button"
                    onClick={() => setFormData({ ...formData, category: "team-enhancement" })}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all duration-200 text-left ${
                      formData.category === "team-enhancement"
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${formData.category === "team-enhancement" ? "bg-blue-100" : "bg-gray-100"}`}>
                      <UserCheck className={`w-5 h-5 ${formData.category === "team-enhancement" ? "text-blue-600" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${formData.category === "team-enhancement" ? "text-blue-900" : "text-gray-700"}`}>Team</p>
                      <p className="text-[11px] text-gray-400">Team dynamics</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Target Date */}
              <div>
                <Label htmlFor="target_date" className="text-sm font-semibold text-gray-700">Target Date *</Label>
                <Input
                  data-testid="goal-date-input"
                  id="target_date"
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                  className="mt-1.5"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <Button
                  onClick={() => {
                    setShowCreate(false);
                    setFormData({ title: "", description: "", target_date: "", category: "personal-enhancement", milestones: [] });
                  }}
                  variant="outline"
                  className="flex-1 text-sm"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="create-goal-btn"
                  onClick={handleCreateGoal}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-sm font-semibold shadow-sm"
                >
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Create Goal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        {/* Edit Goal Dialog */}
        <Dialog open={showEdit} onOpenChange={(open) => {
          setShowEdit(open);
          if (!open) {
            setSelectedGoal(null);
            setFormData({ title: "", description: "", target_date: "", category: "personal-enhancement", milestones: [] });
          }
        }}>
          <DialogContent data-testid="edit-goal-dialog" className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="block text-lg font-bold text-gray-900">Edit Goal</span>
                  <span className="block text-xs font-medium text-gray-400 mt-0.5">Update your goal details</span>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-2">
              <div>
                <Label htmlFor="edit-title" className="text-sm font-semibold text-gray-700">Goal Title *</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Improve Focus Under Pressure"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="edit-description" className="text-sm font-semibold text-gray-700">Description *</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what you want to achieve..."
                  className="mt-1.5"
                  rows={4}
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Category *</Label>
                <div className="grid grid-cols-2 gap-3 mt-1.5">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, category: "personal-enhancement" })}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all duration-200 text-left ${
                      formData.category === "personal-enhancement"
                        ? "border-emerald-500 bg-emerald-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${formData.category === "personal-enhancement" ? "bg-emerald-100" : "bg-gray-100"}`}>
                      <Trophy className={`w-5 h-5 ${formData.category === "personal-enhancement" ? "text-emerald-600" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${formData.category === "personal-enhancement" ? "text-emerald-900" : "text-gray-700"}`}>Personal</p>
                      <p className="text-[11px] text-gray-400">Individual growth</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, category: "team-enhancement" })}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all duration-200 text-left ${
                      formData.category === "team-enhancement"
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${formData.category === "team-enhancement" ? "bg-blue-100" : "bg-gray-100"}`}>
                      <UserCheck className={`w-5 h-5 ${formData.category === "team-enhancement" ? "text-blue-600" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${formData.category === "team-enhancement" ? "text-blue-900" : "text-gray-700"}`}>Team</p>
                      <p className="text-[11px] text-gray-400">Team dynamics</p>
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="edit-target-date" className="text-sm font-semibold text-gray-700">Target Date *</Label>
                <Input
                  id="edit-target-date"
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <Button
                  onClick={() => {
                    setShowEdit(false);
                    setSelectedGoal(null);
                    setFormData({ title: "", description: "", target_date: "", category: "personal-enhancement", milestones: [] });
                  }}
                  variant="outline"
                  className="flex-1 text-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateGoal}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-sm font-semibold"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Update Progress Modal */}
        {selectedGoal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Update Progress</h2>
              <p className="text-gray-600 mb-4">{selectedGoal.title}</p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="progress">Progress ({updateProgress}%)</Label>
                  <input
                    id="progress"
                    type="range"
                    min="0"
                    max="100"
                    value={updateProgress}
                    onChange={(e) => setUpdateProgress(parseInt(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={handleUpdateProgress}
                    className="flex-1 bg-sky-600 hover:bg-sky-700"
                  >
                    Update Progress
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedGoal(null);
                      setUpdateProgress(0);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <AISuggestGoalsDialog
          open={showAISuggest}
          onOpenChange={setShowAISuggest}
          onGoalCreated={fetchGoals}
        />
      </div>
    </div>
  );
}